import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Youtube,
  Heart,
  Star,
  RefreshCw,
} from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Button } from '@/components/ui/button';
import {
  TableCell,
  TableRow
} from '@/components/ui/table';
import { EnrichmentStatusBadge, type EnrichmentStatus } from '../EnrichmentStatusBadge';
import { StatusBadge } from '@/components/status/StatusBadge';
import { formatNumber } from '@/lib/utils';
import { Influencer } from './types';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const getEngagementRateDisplay = (engagementRate: number | null): string => {
  if (engagementRate === null) return '-';
  return `${engagementRate.toFixed(2)}%`;
};

const getAvgLikes = (influencer: Influencer): string => {
  const avgLikes = influencer._db?.avg_likes;
  if (avgLikes && avgLikes > 0) return formatNumber(avgLikes);
  return '-';
};

const getAverageViews = (influencer: Influencer): string => {
  const views = influencer._db?.video_view_count;
  if (views && views > 0) return formatNumber(views);
  return '-';
};

interface InfluencerRowProps {
  influencer: Influencer;
  selectedInfluencers: string[];
  handleSelectInfluencer: (id: string) => void;
  onRefreshInfluencer: (id: string) => Promise<void> | void;
  isRefreshing: boolean;
}

const getInstagramUrl = (influencer: Influencer) => {
  if (!influencer.platforms.includes('Instagram')) return null;
  // If their handle is of form "@somehandle", remove "@"
  const handle = influencer._db?.handle || influencer.handle?.replace(/^@/, '');
  if (!handle) return null;
  return `https://instagram.com/${handle}`;
};

const getYouTubeUrl = (influencer: Influencer) => {
  if (!influencer.platforms.includes('YouTube')) return null;
  // If their handle is of form "@somehandle", remove "@"
  const handle = influencer._db?.handle || influencer.handle?.replace(/^@/, '');
  if (!handle) return null;
  return `https://youtube.com/@${handle}`;
};

export const InfluencerRow: React.FC<InfluencerRowProps> = ({
  influencer,
  selectedInfluencers,
  handleSelectInfluencer,
  onRefreshInfluencer,
  isRefreshing,
}) => {
  const igUrl = getInstagramUrl(influencer);
  const youtubeUrl = getYouTubeUrl(influencer);
  const navigate = useNavigate();

  return (
    <TableRow
      onClick={() => navigate(`/influencers/${influencer.id}`)}
      className="hover:bg-gray-50 group cursor-pointer"
    >
      <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedInfluencers.includes(influencer.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectInfluencer(influencer.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center overflow-hidden">
            {influencer.profilePhoto?.startsWith('http') ? (
              <img src={influencer.profilePhoto} alt={influencer.realName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-semibold text-sm">{influencer.profilePhoto}</span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{influencer.realName}</span>
              <EnrichmentStatusBadge status={influencer._db?.enrichment_status as EnrichmentStatus} />
            </div>
            <div
              className="text-sm text-gray-500 hover:text-purple-600 hover:underline cursor-pointer inline-block transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                if (igUrl) {
                  window.open(igUrl, '_blank');
                } else {
                  navigate(`/influencers/${influencer.id}`);
                }
              }}
            >
              {influencer.handle}
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className="text-center">
        <div className="flex flex-col items-center justify-center space-y-1">
          {influencer.followers.instagram && (
            <div className="flex items-center space-x-1 text-sm justify-center">
              <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-3 h-3" />
              <span>{influencer.followers.instagram}</span>
            </div>
          )}
          {influencer.followers.youtube && (
            <div className="flex items-center space-x-1 text-sm justify-center">
              <Youtube className="w-3 h-3 text-red-500" />
              <span>{influencer.followers.youtube}</span>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center space-x-1">
          <Heart className="w-4 h-4 text-gray-400" />
          <span className="text-sm">{getEngagementRateDisplay(influencer.engagementRate)}</span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <span className="text-sm text-gray-700">
          {getAverageViews(influencer)}
        </span>
      </TableCell>
      {/* <TableCell>
        <div className="flex items-center space-x-1">
          <Star className="w-4 h-4 text-yellow-400 fill-current" />
          <span className="text-sm">{influencer.rating}/5</span>
        </div>
      </TableCell> */}
      <TableCell className="text-center">
        <div className="flex flex-wrap gap-1 justify-center">
          {influencer.tags.slice(0, 2).map((tag: string, index: number) => (
            <span
              key={`${tag}-${index}`}
              className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
            >
              {tag}
            </span>
          ))}
          {influencer.tags.length > 2 && (
            <div onClick={(e) => e.stopPropagation()}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 cursor-pointer">
                      +{influencer.tags.length - 2}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1 p-1">
                      {influencer.tags.slice(2).map((tag, idx) => (
                        <span key={`${tag}-${idx}`} className="text-xs">{tag}</span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isRefreshing}
                onClick={() => onRefreshInfluencer(influencer.id)}
                className="h-8 w-8 text-slate-500 hover:text-purple-600 hover:bg-purple-50"
                aria-label={`Refresh metrics for ${influencer.realName}`}
              >
                {isRefreshing ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Refresh metrics</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      {/* <TableCell>
        {influencer.lastCampaign ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-900 font-medium">{influencer.lastCampaign}</span>
                  {influencer.lastCampaignStatus && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getCampaignStatusColor(influencer.lastCampaignStatus)}`}
                    >
                      {influencer.lastCampaignStatus}
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {influencer.lastCampaignDate 
                    ? `Started on: ${formatDate(influencer.lastCampaignDate)}`
                    : 'Campaign details'
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-sm text-gray-500">No campaigns yet</span>
        )}
      </TableCell> */}
      {/* <TableCell>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(influencer.status)}`}>
          {influencer.status}
        </span>
      </TableCell> */}
    </TableRow>
  );
};

interface InfluencerGridCardProps {
  influencer: Influencer;
  selectedInfluencers: string[];
  handleSelectInfluencer: (id: string) => void;
  getTierColor: (tier: string) => string;
}

export const InfluencerGridCard: React.FC<InfluencerGridCardProps> = (props) => {
  const igUrl = getInstagramUrl(props.influencer);
  const youtubeUrl = getYouTubeUrl(props.influencer);
  const navigate = useNavigate();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 group cursor-pointer"
      onClick={() => navigate(`/influencers/${props.influencer.id}`)}
    >
      {/* Header with checkbox and status */}
      <div className="flex items-start justify-between mb-4">
        <input
          type="checkbox"
          checked={props.selectedInfluencers.includes(props.influencer.id)}
          onChange={(e) => {
            e.stopPropagation();
            props.handleSelectInfluencer(props.influencer.id);
          }}
          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <StatusBadge status={props.influencer.status} className="text-[11px]" />
      </div>

      {/* Profile section */}
      <div className="flex flex-col items-center text-center mb-4">
        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-3 overflow-hidden">
          {props.influencer.profilePhoto?.startsWith('http') ? (
            <img src={props.influencer.profilePhoto} alt={props.influencer.realName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-semibold text-lg">{props.influencer.profilePhoto}</span>
          )}
        </div>
        <div className="flex items-center gap-2 justify-center w-full mb-1">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight">{props.influencer.realName}</h3>
        </div>
        <div className="mb-2">
          <EnrichmentStatusBadge status={props.influencer._db?.enrichment_status as EnrichmentStatus} />
        </div>
        <p className="text-sm text-gray-500 mb-2">{props.influencer.handle}</p>
        <p className="text-xs text-gray-400">{props.influencer.location}</p>
      </div>

      {/* Platforms */}
      <div className="flex justify-center items-center space-x-2 mb-4">
        {props.influencer.platforms.includes('Instagram') && (
          <div className="flex items-center space-x-1 bg-pink-50 text-pink-700 px-2 py-1 rounded-full text-xs">
            <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-3 h-3" />
            <span>{props.influencer.followers.instagram}</span>
          </div>
        )}
        {props.influencer.platforms.includes('YouTube') && (
          <div className="flex items-center space-x-1 bg-red-50 text-red-700 px-2 py-1 rounded-full text-xs">
            <Youtube className="w-3 h-3" />
            <span>{props.influencer.followers.youtube}</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <Heart className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium">{getEngagementRateDisplay(props.influencer.engagementRate)}</span>
          </div>
          <p className="text-xs text-gray-500">Engagement</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <span className="text-sm font-medium text-gray-700">
              {getAverageViews(props.influencer)}
            </span>
          </div>
          <p className="text-xs text-gray-500">Avg Views</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="text-sm font-medium">{props.influencer.rating}/5</span>
          </div>
          <p className="text-xs text-gray-500">Rating</p>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap justify-center gap-1 mb-4">
        {props.influencer.tags.slice(0, 3).map((tag: string, index: number) => (
          <span
            key={`${tag}-${index}`}
            className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
          >
            {tag}
          </span>
        ))}
        {props.influencer.tags.length > 3 && (
          <div onClick={(e) => e.stopPropagation()}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 cursor-pointer">
                    +{props.influencer.tags.length - 3}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-1 p-1">
                    {props.influencer.tags.slice(3).map((tag, idx) => (
                      <span key={`${tag}-${idx}`} className="text-xs">{tag}</span>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {/* Tier badge */}
      <div className="flex justify-center mb-4">
        <span className={`px-3 py-1 text-xs font-medium rounded-full ${props.getTierColor(props.influencer.tier)}`}>
          {props.influencer.tier} Creator
        </span>
      </div>

      {/* Last campaign */}
      {/* <div className="text-center mb-4">
        <p className="text-xs text-gray-500">Last Campaign</p>
        {props.influencer.lastCampaign ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center space-x-1 mt-1">
                  <p className="text-sm font-medium text-gray-700">{props.influencer.lastCampaign}</p>
                  {props.influencer.lastCampaignStatus && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getCampaignStatusColor(props.influencer.lastCampaignStatus)}`}
                    >
                      {props.influencer.lastCampaignStatus}
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {props.influencer.lastCampaignDate 
                    ? `Started on: ${formatDate(props.influencer.lastCampaignDate)}`
                    : 'Campaign details'
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <p className="text-sm font-medium text-gray-500 mt-1">No campaigns yet</p>
        )}
      </div> */}

      {/* Action buttons - Show Instagram and YouTube links if available */}
      <div className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
        {igUrl && (
          <a
            href={igUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="View Instagram Profile"
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="outline" size="sm" className="hover:bg-pink-50">
              <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-4 h-4" />
            </Button>
          </a>
        )}
        {youtubeUrl && (
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="View YouTube Profile"
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="outline" size="sm" className="hover:bg-red-50">
              <Youtube className="w-4 h-4 text-red-500" />
            </Button>
          </a>
        )}
      </div>
    </div>
  );
};

